import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';
import type { LocationQueryValue, RouteLocationNormalized } from 'vue-router';
import { useStore } from 'vuex';
import { useConfirmWithOptions, useUnsavedConfirm } from '@/composable/message-dialog';
import { key } from '@/store/index';

type TCallTargetIdInQuery = LocationQueryValue | LocationQueryValue[] | undefined;

/**
 * URLのcallTargetIdの解釈はここが唯一の規則。
 * モーダルの表示判定とルートガードの判定で必ず共有する。
 */
const parseCallTargetIdInQuery = (raw: TCallTargetIdInQuery): number | null => {
  if (raw == null || Array.isArray(raw)) return null;
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
};

/**
 * リードモーダルを閉じる方向のナビゲーションか。
 * リードを開く・前後送り・モーダル内のページ送りはfalseになる。
 */
const isLeadModalClosingNavigation = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
): boolean => parseCallTargetIdInQuery(from.query.callTargetId) != null
  && parseCallTargetIdInQuery(to.query.callTargetId) == null;

/**
 * リードモーダルの「閉じる」を一元管理する。
 *
 * 不変条件はモーダルの表示 ⇔ URLにcallTargetIdが存在すること。
 * 閉じる操作(✕ボタン・ブラウザバック)はすべて「URLからcallTargetIdを外すナビゲーション」に集約し、
 * 確認ダイアログはonBeforeRouteUpdateという単一の地点だけが出す。
 * $modal.hide()の側で確認するとURL変更が二重に走り、確認が2回出るうえ、
 * 2回目をキャンセルするとURLと表示が乖離する。
 */
const useLeadModalClose = () => {
  const route = useRoute();
  const router = useRouter();
  const store = useStore(key);
  const i18n = useI18n();
  const { unsavedConfirm } = useUnsavedConfirm();
  const { confirmWithOptions } = useConfirmWithOptions();

  // NOTE: 開いているリードはURLを唯一の情報源とする。
  // リンククリック・前後送り・ブラウザバック/フォワード・リロードのすべてがこの値の変化に集約される。
  const callTargetIdInUrl = computed(() => parseCallTargetIdInQuery(route.query.callTargetId));

  const confirmLeadModalClose = async (): Promise<boolean> => {
    if (!store.getters['userUi/isClosingAndMovingLeadEnabled']) {
      return await confirmWithOptions({
        title: i18n.t('general.confirm.text'),
        message: i18n.t('callTarget.close.isCalling'),
        buttonsCancel: true,
        closeOnClickOutside: false,
      });
    }
    if (store.getters['userUi/isTargetEditing']) {
      return await unsavedConfirm();
    }
    return true;
  };

  /**
   * モーダルを閉じる唯一の入口。
   * 確認はルートガードが行い、実際の$modal.hide()はcallTargetIdInUrlのwatchが行う。
   */
  const requestLeadModalClose = () => {
    const query = { ...route.query };
    delete query.callTargetId;
    delete query.historyItem;
    return router.push({ query });
  };

  onBeforeRouteUpdate(async (to, from) => {
    if (!isLeadModalClosingNavigation(to, from)) return;
    if (await confirmLeadModalClose()) return;
    return false;
  });

  // NOTE: 既存の挙動を変えないため、通話中の離脱は許容する(/app/accounts への遷移を想定。仕様次第で変更可)
  onBeforeRouteLeave(async () => {
    if (!store.getters['userUi/isClosingAndMovingLeadEnabled']) return;
    if (await confirmLeadModalClose()) return;
    return false;
  });

  return {
    callTargetIdInUrl,
    requestLeadModalClose,
  };
};

export {
  useLeadModalClose,
};
